/* eslint-disable complexity */
import React, { useEffect, useState } from "react";
import { Col, Row } from "antd";
import { googleAds } from "appConfig";
import { useLocation } from "react-router-dom";

import styles from "./adList.scss";
import { AdCard } from "../AdCard";
import PromotedSellerCard from "../PromotedSellerCard/PromotedSellerCard";

// Position in the ad list where promoted seller card should appear (1-indexed)
const PROMOTED_SELLER_POSITION = 7;
import { useDispatch, useSelector } from "react-redux";
import {
  selectCategories,
  selectModules
} from "../../containers/App/selectors";
import { setForbidAds, selectForbidAds, selectIsAgeVerified } from "../../routes/Ad/ad.ducks";
import AdListLoader from "../AdListLoader";
import GoogleAd from "../GoogleAd";
import AgeVerification from "../AgeVerificationModal";
import { checkEmptyArray, api, getUrlParams } from "../../services";
import { QueryParam } from "../../models";
import { TwoGoogleAds } from "./TwoGoogleAds";
import { RealEstateWidget } from "../../containers/ModulePage/RealEstatePricesEntryBox/RealEstateWidget";

const defaultRenderGoogleAds = (
  index: any,
  isMobile: any,
  showGoogleAds: any,
  forbiddAds: any,
) => {
  if (!showGoogleAds) {
    return null;
  }

  if (isMobile) {
    // show different google ads each 5 unit
    switch (index) {
      case 5:
        return forbiddAds ? null : (
          <div className={styles.adsWrapper}>
            <GoogleAd
              className={styles.adsMobile}
              config={{
                ...googleAds.searchResultMobile.box1,
                id: "googleAds.searchResultMobile.box1"
              }}
              placeholderHeight={250}
            />
          </div>
        );

      case 10:
        return forbiddAds ? null : (
          <div className={styles.adsWrapper}>
            <GoogleAd
              className={styles.adsMobile}
              config={{
                ...googleAds.searchResultMobile.box2,
                id: "googleAds.searchResultMobile.box2"
              }}
              placeholderHeight={250}
            />
          </div>
        );

      case 15:
        return forbiddAds ? null : (
          <div className={styles.adsWrapper}>
            <GoogleAd
              className={styles.adsMobile}
              config={{
                ...googleAds.searchResultMobile.box3,
                id: "googleAds.searchResultMobile.box3"
              }}
              placeholderHeight={250}
            />
          </div>
        );

      case 20:
        return forbiddAds ? null : (
          <div className={styles.adsWrapper}>
            <GoogleAd
              className={styles.adsMobile}
              config={{
                ...googleAds.searchResultMobile.box4,
                id: "googleAds.searchResultMobile.box4"
              }}
              placeholderHeight={250}
            />
          </div>
        );

      default:
        return null;
    }
  }

  if (index % 3 === 2) {
    return forbiddAds ? null : (
      <TwoGoogleAds index={index} />
    );
  }

  return null;
};

function AdList(props) {
  const {
    ads,
    responsiveProps,
    adCardProps,
    gutter,
    AdCardComponent,
    emptyAdsText,
    className,
    showGoogleAds,
    renderGoogleAds,
    category,
    module,
    showDetails,
    bottomSection,
    isLoading
  } = props;

  const forbiddAds = useSelector(selectForbidAds);
  const isAgeVerified = useSelector(selectIsAgeVerified);
  const dispatch = useDispatch();
  const [ageVerificationModal, setAgeVerificationModal] = useState(false);
  const modules = useSelector(selectModules);
  const categories = useSelector(selectCategories);
  const [promotedSeller, setPromotedSeller] = useState(null);
  const location = useLocation();

  // Fetch promoted seller from API
  useEffect(() => {
    setPromotedSeller(null);

    if (!module || !category) {
      return;
    }

    const searchParams = getUrlParams(location.search, QueryParam.searchQuery);
    const locationId = searchParams && searchParams.includeCityIds
      ? (Array.isArray(searchParams.includeCityIds) ? searchParams.includeCityIds[0] : searchParams.includeCityIds)
      : undefined;

    let cancelled = false;

    api.FeaturedStores.slot(module, category, locationId, () => null)
      .then((data) => {
        if (cancelled) return;
        setPromotedSeller(data);
      })
      .catch(() => {
        if (cancelled) return;
        setPromotedSeller(null);
      });

    return () => {
      cancelled = true;
    };
  }, [module, category, location.search]);

  useEffect(() => {
    setForbiddenAds();
  }, [modules, categories, ads]);

  const setForbiddenAds = () => {
    modules.forEach(mod => {
      if (mod.name == module && mod.is18Old) {
        dispatch(setForbidAds(true));
        setAgeVerificationModal(true);
        return;
      }
    });

    categories.forEach(cat => {
      if (cat.route == category && cat.is18Old) {
        dispatch(setForbidAds(true));
        setAgeVerificationModal(true);
        return;
      }
    });

    // Check if any ad in search results belongs to an 18+ category
    if (ads && ads.length > 0 && categories.length > 0) {
      const is18OldCategories = categories
        .filter(cat => cat.is18Old)
        .map(cat => cat.route);

      const hasAdFrom18PlusCategory = ads.some(
        (ad: any) => is18OldCategories.includes(ad.category)
      );

      if (hasAdFrom18PlusCategory) {
        dispatch(setForbidAds(true));
      } else {
        dispatch(setForbidAds(false));
      }
    }
  };

  // if AgeVerification state is not calculated yet, we should not render the ads
  if (!ageVerificationModal && (!checkEmptyArray(categories) || !checkEmptyArray(modules))) {
    return null;
  }


  if (ageVerificationModal && isAgeVerified !== true) {
    return <AgeVerification />;
  }

  return ads ? ( //eslint-disable-line
    isLoading ? (
      // AdListLoader
      <AdListLoader isMobile={adCardProps.isMobile} />
    ) : (
      ads.length === 0 ? (
        <div className={styles.noAds}>
          <h2>{emptyAdsText}</h2>
        </div>
      ) : (
        <Row gutter={gutter} type="flex" className={`${className} ${styles.row}`}>
          {ads.map((ad: any, index: any) => {
            const showSellerCard = promotedSeller && index === PROMOTED_SELLER_POSITION - 1;

            return (
              <React.Fragment key={ad.id}>
                {showSellerCard && (
                  <Col {...responsiveProps}>
                    <PromotedSellerCard
                      seller={promotedSeller}
                      isMobile={adCardProps.isMobile}
                    />
                  </Col>
                )}
                <Col {...responsiveProps}>
                  {renderGoogleAds(
                    index,
                    adCardProps.isMobile,
                    showGoogleAds,
                    forbiddAds
                  )}
                  <AdCardComponent
                    ad={ad}
                    {...adCardProps}
                    category={category}
                    showDetails={showDetails}
                    bottomSection={bottomSection}
                    listIndex={index}
                  />
                </Col>
              </React.Fragment>
            );
          })}
        </Row>
      )
    )
  ) : (
    // AdListLoader
    <AdListLoader isMobile={adCardProps.isMobile} />
  );


}

AdList.defaultProps = {
  adCardProps: {},
  responsiveProps: {},
  gutter: 0,
  AdCardComponent: AdCard,
  emptyAdsText: "Ovdje nema oglasa!",
  className: "",
  showGoogleAds: false,
  renderGoogleAds: defaultRenderGoogleAds
};

export default AdList;



// WEBPACK FOOTER //
// ./src/components/AdList/index.tsx